import { runTransaction, serverTimestamp } from 'firebase/firestore';

const CHAT_SEARCH_INDEX_VERSION = 1;
const CHAT_SEARCH_PREVIEW_LIMIT = 8;
const CHAT_SEARCH_PREVIEW_TEXT_LIMIT = 220;
const CHAT_SEARCH_TEXT_LIMIT = 3000;

const stripMarkdownFormatting = (value = '') =>
  String(value || '')
    .replace(/!\[([^\]]*)\]\((?:<[^>]+>|[^)]+)\)/g, '$1')
    .replace(/\[([^\]]+)\]\((?:<[^>]+>|[^)]+)\)/g, '$1')
    .replace(/!\[([^\]]*)\]\(<[^>\s]+>?[^)]*\)?/g, '$1')
    .replace(/<https?:\/\/[^>\s]+>/g, '')
    .replace(/https?:\/\/\S+/g, '')
    .replace(/`{1,3}([^`]+)`{1,3}/g, '$1')
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(.*?)\1/g, '$2')
    .replace(/~~(.*?)~~/g, '$1')
    .replace(/(^|\s)[#>*-]{1,3}\s+/g, '$1');

export const normalizeSearchText = (value = '') =>
  stripMarkdownFormatting(value)
    .replace(/\s+/g, ' ')
    .trim();

const truncateSearchText = (value = '', limit = CHAT_SEARCH_PREVIEW_TEXT_LIMIT) => {
  const text = normalizeSearchText(value);
  return text.length > limit ? `${text.slice(0, limit - 3)}...` : text;
};

const getAttachmentPreviewText = (message = {}) => {
  const attachments = Array.isArray(message.attachments) ? message.attachments : [];
  const firstAttachment = attachments.find(Boolean);
  if (firstAttachment?.name) return firstAttachment.name;

  if (message.imageUrl) return 'Image';
  if (message.pdfUrl) return message.pdfName || 'PDF Document';
  if (message.audioUrl) return message.audioName || 'Audio Recording';
  if (message.videoUrl) return message.videoName || 'Video';

  return '';
};

export const buildMessageSearchPreview = (message = {}) => {
  const text = truncateSearchText(message.text || getAttachmentPreviewText(message));
  if (!text) return null;

  return {
    id: message.id || '',
    role: message.role || 'message',
    text,
  };
};

export const normalizeRecentMessagePreviews = (previews = []) => {
  if (!Array.isArray(previews)) return [];

  return previews
    .map((preview) => {
      if (typeof preview === 'string') {
        const text = truncateSearchText(preview);
        return text ? { id: '', role: 'message', text } : null;
      }
      if (!preview || typeof preview !== 'object') return null;
      const text = truncateSearchText(preview.text);
      if (!text) return null;
      return {
        id: typeof preview.id === 'string' ? preview.id : '',
        role: typeof preview.role === 'string' ? preview.role : 'message',
        text,
      };
    })
    .filter(Boolean)
    .slice(0, CHAT_SEARCH_PREVIEW_LIMIT);
};

export const buildChatSearchIndexText = (chat = {}) => {
  const previewTexts = normalizeRecentMessagePreviews(chat.recentMessagePreviews)
    .map((preview) => preview.text);
  const parts = [
    chat.topic,
    chat.model,
    chat.projectName,
    chat.lastMessagePreview,
    ...previewTexts,
  ];

  return normalizeSearchText(parts.filter(Boolean).join(' '))
    .toLowerCase()
    .slice(0, CHAT_SEARCH_TEXT_LIMIT);
};

export const buildChatSearchIndexUpdate = (chat = {}) => ({
  searchIndexText: buildChatSearchIndexText(chat),
  searchIndexVersion: CHAT_SEARCH_INDEX_VERSION,
  searchUpdatedAt: serverTimestamp(),
});

export const buildChatSearchIndexUpdateFromMessages = ({ chat = {}, messages = [] } = {}) => {
  const recentMessagePreviews = [...messages]
    .reverse()
    .map(buildMessageSearchPreview)
    .filter(Boolean)
    .slice(0, CHAT_SEARCH_PREVIEW_LIMIT);
  const nextChat = {
    ...chat,
    recentMessagePreviews,
    lastMessagePreview: recentMessagePreviews[0]?.text || '',
  };

  return {
    lastMessagePreview: nextChat.lastMessagePreview,
    recentMessagePreviews,
    ...buildChatSearchIndexUpdate(nextChat),
  };
};

export const updateChatSearchIndexForMessage = async ({ db, chatRef, message }) => {
  const preview = buildMessageSearchPreview(message);

  await runTransaction(db, async (transaction) => {
    const chatSnapshot = await transaction.get(chatRef);
    const chat = chatSnapshot.data() || {};
    const existingPreviews = normalizeRecentMessagePreviews(chat.recentMessagePreviews);
    const withoutCurrentMessage = existingPreviews.filter(
      (item) => item.id && item.id !== message.id
    );
    const recentMessagePreviews = preview
      ? [preview, ...withoutCurrentMessage].slice(0, CHAT_SEARCH_PREVIEW_LIMIT)
      : withoutCurrentMessage.slice(0, CHAT_SEARCH_PREVIEW_LIMIT);
    const nextChat = {
      ...chat,
      recentMessagePreviews,
      lastMessagePreview: recentMessagePreviews[0]?.text || '',
    };

    transaction.update(chatRef, {
      lastMessageSent: serverTimestamp(),
      lastMessagePreview: nextChat.lastMessagePreview,
      recentMessagePreviews,
      ...buildChatSearchIndexUpdate(nextChat),
    });
  });
};
